import { MarketGroupDto, MarketGroupSessionDto } from '../../../models/market-groups-api.model';
import {
	MarketCountdown,
	MarketClockState,
	MarketDaySchedule,
	MarketTimeLabel,
	MarketTimeLabels,
	MarketTimeSlicerSegment,
	ScheduledTradingPeriod,
	TradingDayPeriods,
	TradingPeriod
} from '../app-clock.model';

/** Market group shown by the header clock when no symbol is in context. */
export const DEFAULT_MARKET_GROUP_ID = 1;

export const EXCHANGE_TIME_ZONE = 'Asia/Tehran';

// Built once: `Intl.DateTimeFormat` construction is costly and these run on every clock tick.
const EXCHANGE_WEEKDAY_FORMAT = new Intl.DateTimeFormat('en-US', { timeZone: EXCHANGE_TIME_ZONE, weekday: 'short' });
const UTC_WEEKDAY_FORMAT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' });
const EXCHANGE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
	timeZone: EXCHANGE_TIME_ZONE,
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hourCycle: 'h23'
});
const EXCHANGE_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
	timeZone: EXCHANGE_TIME_ZONE,
	year: 'numeric',
	month: 'numeric',
	day: 'numeric'
});
const EXCHANGE_DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
	timeZone: EXCHANGE_TIME_ZONE,
	year: 'numeric',
	month: 'numeric',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hourCycle: 'h23'
});

const COUNTDOWN_TRANSITION_LABELS: Record<MarketClockState, string> = {
	preTrading: 'شروع دوره پیش گشایش',
	trading: 'شروع معاملات',
	closed: 'بسته شدن بازار'
};

/** A session's boundaries in seconds since exchange midnight. */
interface SessionSeconds {
	readonly preOpen: number;
	readonly open: number;
	readonly close: number;
}

/** One weekday of a group's schedule, parsed once and reused on every clock tick. */
interface DaySchedule {
	readonly sessions: readonly SessionSeconds[];
	readonly periods: readonly ScheduledTradingPeriod[];
	/** `periods` without the seconds; a stable reference, so signals reading it don't re-fire every tick. */
	readonly displayPeriods: readonly TradingPeriod[];
}

const EMPTY_DAY_SCHEDULE: DaySchedule = { sessions: [], periods: [], displayPeriods: [] };

/**
 * Parsed weekly schedules keyed by the group DTO. DTOs are immutable and replaced wholesale on
 * reload, so an entry never goes stale and the old ones are garbage-collected with their DTO.
 */
const weeklyScheduleCache = new WeakMap<MarketGroupDto, ReadonlyMap<string, DaySchedule>>();

function dayScheduleFor(group: MarketGroupDto | undefined, weekday: string): DaySchedule {
	if (!group) {
		return EMPTY_DAY_SCHEDULE;
	}

	let weekly = weeklyScheduleCache.get(group);
	if (!weekly) {
		weekly = buildWeeklySchedule(group);
		weeklyScheduleCache.set(group, weekly);
	}

	return weekly.get(weekday) ?? EMPTY_DAY_SCHEDULE;
}

function buildWeeklySchedule(group: MarketGroupDto): ReadonlyMap<string, DaySchedule> {
	const weekly = new Map<string, DaySchedule>();
	for (const { day, sessions } of group.time ?? []) {
		// First entry wins for a repeated day.
		if (day && !weekly.has(day)) {
			weekly.set(day, buildDaySchedule(parseSessions(sessions)));
		}
	}

	return weekly;
}

function buildDaySchedule(sessions: readonly SessionSeconds[]): DaySchedule {
	const periods: ScheduledTradingPeriod[] = [];
	for (const { preOpen, open, close } of sessions) {
		if (preOpen < open) {
			periods.push({
				type: 'preTrading',
				startSeconds: preOpen,
				endSeconds: open,
				start: formatTime(preOpen),
				end: formatTime(open)
			});
		}
		if (open < close) {
			periods.push({
				type: 'trading',
				startSeconds: open,
				endSeconds: close,
				start: formatTime(open),
				end: formatTime(close)
			});
		}
	}

	return { sessions, periods, displayPeriods: periods.map(({ type, start, end }) => ({ type, start, end })) };
}

/** Parses and validates a day's sessions, dropping malformed or out-of-order ones, sorted by pre-open. */
function parseSessions(sessions: readonly MarketGroupSessionDto[] | undefined): SessionSeconds[] {
	const parsed: SessionSeconds[] = [];
	for (const session of sessions ?? []) {
		const preOpen = timeToSeconds(session.preOpen);
		const open = timeToSeconds(session.open);
		const close = timeToSeconds(session.close);
		if (preOpen !== null && open !== null && close !== null && preOpen <= open && open <= close) {
			parsed.push({ preOpen, open, close });
		}
	}

	return parsed.sort((first, second) => first.preOpen - second.preOpen);
}

/** Indexes `formatToParts` output once instead of scanning it per field. */
function numericParts(parts: readonly Intl.DateTimeFormatPart[]): (type: Intl.DateTimeFormatPartTypes) => number {
	const values = new Map(parts.map(part => [part.type, Number(part.value)]));
	return type => values.get(type) ?? Number.NaN;
}

export function marketGroupFor(
	groups: readonly MarketGroupDto[],
	groupId: number | null | undefined
): MarketGroupDto | undefined {
	if (groupId === null || groupId === undefined || !Number.isFinite(groupId)) {
		return undefined;
	}

	return groups.find(candidate => candidate.groupId === groupId);
}

const TRADING_DAY_LABELS: ReadonlyMap<string, { order: number; label: string }> = new Map([
	['Sat', { order: 1, label: 'شنبه' }],
	['Sun', { order: 2, label: 'یکشنبه' }],
	['Mon', { order: 3, label: 'دوشنبه' }],
	['Tue', { order: 4, label: 'سه‌شنبه' }],
	['Wed', { order: 5, label: 'چهارشنبه' }],
	['Thu', { order: 6, label: 'پنجشنبه' }],
	['Fri', { order: 7, label: 'جمعه' }]
]);

export function tradingDaysFor(group: MarketGroupDto | undefined): string {
	const tradingDays = new Map<number, string>();
	for (const schedule of group?.time ?? []) {
		const day = schedule.day ? TRADING_DAY_LABELS.get(schedule.day) : undefined;
		if (day && (schedule.sessions?.length ?? 0) > 0) {
			tradingDays.set(day.order, day.label);
		}
	}

	const days = [...tradingDays.entries()]
		.sort(([firstOrder], [secondOrder]) => firstOrder - secondOrder)
		.map(([, label]) => label);

	if (days.length === 0) {
		return '';
	}

	return days.length === 1 ? days[0] : `${days[0]} تا ${days[days.length - 1]}`;
}

/** Calendar order, so stepping forward from any weekday wraps through the week. */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Today's display periods or, when today has no sessions, those of the next weekday that does —
 * so a closed day still shows when the market next trades. `null` when the group never trades.
 */
export function tradingDayPeriodsFor(group: MarketGroupDto | undefined, now: Date): TradingDayPeriods | null {
	const today = weekdayFor(now);
	const todayIndex = WEEKDAYS.findIndex(weekday => weekday === today);
	if (!group || todayIndex < 0) {
		return null;
	}

	for (let offset = 0; offset < WEEKDAYS.length; offset++) {
		const weekday = WEEKDAYS[(todayIndex + offset) % WEEKDAYS.length];
		const { displayPeriods } = dayScheduleFor(group, weekday);
		if (displayPeriods.length > 0) {
			return {
				dayLabel: TRADING_DAY_LABELS.get(weekday)?.label ?? weekday,
				isToday: offset === 0,
				periods: displayPeriods
			};
		}
	}

	return null;
}

/** Calculates today's display periods and market state from one parsed session schedule. */
export function marketScheduleForToday(group: MarketGroupDto | undefined, now: Date): MarketDaySchedule {
	const weekday = weekdayFor(now);
	if (!weekday) {
		return { state: 'closed', periods: EMPTY_DAY_SCHEDULE.displayPeriods };
	}

	const { periods, displayPeriods } = dayScheduleFor(group, weekday);
	const currentSeconds = exchangeSecondsFor(now);
	const activePeriod =
		currentSeconds === null
			? undefined
			: periods.find(period => currentSeconds >= period.startSeconds && currentSeconds < period.endSeconds);

	return { state: activePeriod?.type ?? 'closed', periods: displayPeriods };
}

function weekdayFor(now: Date): string | null {
	if (!Number.isFinite(now.getTime())) {
		return null;
	}

	return EXCHANGE_WEEKDAY_FORMAT.format(now);
}

function exchangeSecondsFor(now: Date): number | null {
	const part = numericParts(EXCHANGE_TIME_FORMAT.formatToParts(now));
	const hour = part('hour');
	const minute = part('minute');
	const second = part('second');
	if (!Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) {
		return null;
	}

	return hour * 3600 + minute * 60 + second;
}

/**
 * Finds the next transition for the group's schedule — pre-open, open, or close — and the state the
 * market enters at it. A session without a pre-open window counts down straight to the open.
 */
export function nextMarketChange(group: MarketGroupDto | undefined, now: Date): MarketCountdown | null {
	const nowMs = now.getTime();
	if (!group?.time?.length || !Number.isFinite(nowMs)) {
		return null;
	}

	const currentDate = exchangeDateParts(now);
	if (!currentDate) {
		return null;
	}

	for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
		const weekday = UTC_WEEKDAY_FORMAT.format(
			new Date(Date.UTC(currentDate.year, currentDate.month - 1, currentDate.day + dayOffset, 12))
		);
		const at = (seconds: number) => exchangeTimeOnDate(currentDate, dayOffset, seconds);

		for (const session of dayScheduleFor(group, weekday).sessions) {
			const preOpen = at(session.preOpen);
			if (nowMs < preOpen.getTime()) {
				return { target: preOpen, next: session.preOpen < session.open ? 'preTrading' : 'trading' };
			}

			const open = at(session.open);
			if (nowMs < open.getTime()) {
				return { target: open, next: 'trading' };
			}

			const close = at(session.close);
			if (nowMs < close.getTime()) {
				return { target: close, next: 'closed' };
			}
		}
	}

	return null;
}

export function formatMarketCountdown(countdown: MarketCountdown, now: Date): string {
	const remainingMinutes = Math.ceil((countdown.target.getTime() - now.getTime()) / 60_000);
	if (!Number.isFinite(remainingMinutes) || remainingMinutes <= 0) {
		return '';
	}

	const days = Math.floor(remainingMinutes / 1440);
	const hours = Math.floor((remainingMinutes % 1440) / 60);
	const minutes = remainingMinutes % 60;
	const parts = [
		days > 0 ? `${days} روز` : '',
		hours > 0 ? `${hours} ساعت` : '',
		minutes > 0 ? `${minutes} دقیقه` : ''
	].filter(Boolean);

	return `${parts.join(' و ')} تا ${COUNTDOWN_TRANSITION_LABELS[countdown.next]}`;
}

function exchangeDateParts(date: Date): { year: number; month: number; day: number } | null {
	const part = numericParts(EXCHANGE_DATE_FORMAT.formatToParts(date));
	const year = part('year');
	const month = part('month');
	const day = part('day');

	return Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day) ? { year, month, day } : null;
}

function exchangeTimeOnDate(
	date: { year: number; month: number; day: number },
	dayOffset: number,
	timeSeconds: number
): Date {
	const utcGuess = Date.UTC(
		date.year,
		date.month - 1,
		date.day + dayOffset,
		Math.floor(timeSeconds / 3600),
		Math.floor((timeSeconds % 3600) / 60),
		timeSeconds % 60
	);
	const part = numericParts(EXCHANGE_DATE_TIME_FORMAT.formatToParts(new Date(utcGuess)));
	const localAsUtc = Date.UTC(
		part('year'),
		part('month') - 1,
		part('day'),
		part('hour'),
		part('minute'),
		part('second')
	);

	return new Date(utcGuess + utcGuess - localAsUtc);
}

/** Parses `HH:mm` or `HH:mm:ss` to seconds since midnight; `null` when malformed. */
export function timeToSeconds(value: string): number | null {
	const match = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(value);
	if (!match) {
		return null;
	}

	return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] ?? 0);
}

function formatTime(seconds: number): string {
	const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
	const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');

	return `${hours}:${minutes}`;
}

export function timeToMinutes(time: string): number | null {
	const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time);
	if (!match) {
		return null;
	}

	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (hour > 23 || minute > 59) {
		return null;
	}

	return hour * 60 + minute;
}

/**
 * Boundary labels for the time slicer: pre-open starts above the bar, open and close times below
 * it. Each label reuses the `left` of the segment starting at that time, so it lines up with the
 * drawn bar — gaps and the pre-trading minimum width included — at any width, with no measuring.
 */
export function marketTimeLabelsFor(
	periods: readonly TradingPeriod[],
	segments: readonly MarketTimeSlicerSegment[]
): MarketTimeLabels {
	const leftByStartMinutes = new Map(segments.map(segment => [segment.startMinutes, segment.left]));
	const labelAt = (time: string): MarketTimeLabel | undefined => {
		const minutes = timeToMinutes(time);
		const left = minutes === null ? undefined : leftByStartMinutes.get(minutes);
		return left ? { time, left } : undefined;
	};

	const upper: MarketTimeLabel[] = [];
	const lower = new Map<string, MarketTimeLabel>();
	for (const period of periods) {
		if (period.type === 'preTrading') {
			const label = labelAt(period.start);
			if (label) {
				upper.push(label);
			}
			continue;
		}

		for (const time of [period.start, period.end]) {
			const label = labelAt(time);
			if (label) {
				lower.set(time, label);
			}
		}
	}

	// `HH:mm` sorts chronologically as a string.
	return { upper, lower: [...lower.values()].sort((first, second) => first.time.localeCompare(second.time)) };
}

const MIN_PRE_TRADING_SEGMENT_WIDTH_PX = 20;

export function timeSliceSegments(periods: readonly TradingPeriod[]): MarketTimeSlicerSegment[] {
	const _periods = periods
		.map(period => ({
			...period,
			startMinutes: timeToMinutes(period.start),
			endMinutes: timeToMinutes(period.end)
		}))
		.filter(
			(period): period is typeof period & { startMinutes: number; endMinutes: number } =>
				period.startMinutes !== null && period.endMinutes !== null && period.startMinutes < period.endMinutes
		)
		.sort((first, second) => first.startMinutes - second.startMinutes);

	const timeline: Array<{
		type: 'closed' | TradingPeriod['type'];
		startMinutes: number;
		endMinutes: number;
	}> = [];
	let cursorMinutes = 0;

	for (const period of _periods) {
		if (period.startMinutes > cursorMinutes) {
			timeline.push({ type: 'closed', startMinutes: cursorMinutes, endMinutes: period.startMinutes });
		}

		const sessionStart = Math.max(cursorMinutes, period.startMinutes);
		if (period.endMinutes > sessionStart) {
			timeline.push({
				type: period.type,
				startMinutes: sessionStart,
				endMinutes: period.endMinutes
			});
			cursorMinutes = period.endMinutes;
		}
	}

	if (timeline.length === 0) {
		timeline.push({ type: 'closed', startMinutes: 0, endMinutes: 1440 });
	} else if (cursorMinutes < 1440) {
		timeline.push({ type: 'closed', startMinutes: cursorMinutes, endMinutes: 1440 });
	}

	const gapPx = 2;
	// Extra width each earlier pre-trading segment took to reach its minimum; it pushes every later
	// segment right. Accumulated as we go rather than re-sliced per segment.
	const priorMinimumWidthAdjustments: string[] = [];

	return timeline.map((segment, index) => {
		const durationPercent = ((segment.endMinutes - segment.startMinutes) / 1440) * 100;
		const gapAfterSegment = index < timeline.length - 1 ? gapPx : 0;
		const leftOffset = [`${index * gapPx}px`, ...priorMinimumWidthAdjustments].join(' + ');
		const left = `calc(${(segment.startMinutes / 1440) * 100}% + ${leftOffset})`;
		const width =
			segment.type === 'preTrading'
				? `max(calc(${durationPercent}% - ${gapAfterSegment}px), ${MIN_PRE_TRADING_SEGMENT_WIDTH_PX}px)`
				: index === timeline.length - 1 && priorMinimumWidthAdjustments.length > 0
					? `max(0px, calc(${durationPercent}% - ${priorMinimumWidthAdjustments.join(' - ')}))`
					: `calc(${durationPercent}% - ${gapAfterSegment}px)`;

		if (segment.type === 'preTrading') {
			priorMinimumWidthAdjustments.push(
				`max(0px, calc(${MIN_PRE_TRADING_SEGMENT_WIDTH_PX}px - calc(${durationPercent}% - ${gapAfterSegment}px)))`
			);
		}

		return { type: segment.type, startMinutes: segment.startMinutes, left, width };
	});
}
